import {
  applyPendoIdentity,
  buildCoordinatorPendoConfig,
  buildGuestPendoConfig,
} from '@eos/event-management-feature/pendo/guestPendo';
import { Session } from 'next-auth';
import { useEffect } from 'react';

export const useInitializePendo = ({
  session,
  host,
  organisationCode,
  ready = true,
}: {
  session: Session | null;
  host?: string;
  organisationCode?: string;
  ready?: boolean;
}) => {
  useEffect(() => {
    if (!ready) return;
    // The Pendo script is loaded in Pendo.tsx, but we need to wait for it to be ready.
    if (typeof window !== 'undefined' && window.pendo) {
      const isCoordinator =
        session?.user?.role?.includes('coordinator') ?? false;
      // Guest sessions are issued with role 'guest' (see guestpass-access-token
      // provider); 'traveller' is kept as a defensive alias.
      const isGuest =
        (session?.user?.role?.includes('guest') ||
          session?.user?.role?.includes('traveller')) ??
        false;

      const tenantId = session?.user?.tenantId;

      // For guest sessions, session.user.userId is the EMS guest id.
      const pendoConfig = isCoordinator
        ? buildCoordinatorPendoConfig({
            tenantId,
            solUserId: session?.user?.userId,
            organisationCode,
            host,
          })
        : isGuest
          ? buildGuestPendoConfig({
              tenantId,
              guestPassGuestId: session?.user?.userId,
              refUserId: session?.user?.refUserId,
              organisationCode: session?.user?.orgCode,
              host,
            })
          : undefined;

      if (pendoConfig) {
        applyPendoIdentity(pendoConfig);

        // Wait for Pendo to be fully ready (including loading guides)
        let retryCount = 0;
        const maxRetries = 50; // 5 seconds max (50 * 100ms)

        const checkPendoReady = () => {
          const isPendoReady =
            window.pendo &&
            typeof window.pendo.showGuideById === 'function' &&
            window.pendo.guides &&
            Array.isArray(window.pendo.guides);

          if (isPendoReady) {
            window.dispatchEvent(new Event('pendo:ready'));
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkPendoReady, 100);
          } else {
            console.warn(
              '[initializePendo] Pendo failed to fully initialize after 5 seconds'
            );
          }
        };

        checkPendoReady();
      }
    }
  }, [session, host, organisationCode, ready]);
};
